// RBAC Permission Configuration for URBANHUB
// Defines which roles can access which modules and actions

export type AppRole =
  | 'admin' |'hr_manager' |'payroll' |'finance' |'management' |'department_head' |'employee' |'approver' |'read_only' | 'compliance';

export interface RolePermissions {
  // Navigation modules
  canAccessDashboard: boolean;
  canAccessDirectory: boolean;
  canAccessHR: boolean;
  canAccessAttendance: boolean;
  canAccessPayroll: boolean;
  canAccessDocuments: boolean;
  canAccessContracts: boolean;
  canAccessAssets: boolean;
  canAccessApprovals: boolean;
  canAccessReports: boolean;
  canAccessAudit: boolean;
  canAccessMasterData: boolean;
  canAccessCompanySetup: boolean;
  canAccessSettings: boolean;
  canAccessESS: boolean;
  canAccessCompliance: boolean;
  canAccessContacts: boolean;
  // Sensitive module sub-permissions
  canViewSalaryDetails: boolean;
  canRunPayroll: boolean;
  canManageContracts: boolean;
  canEditMasterData: boolean;
  canManageUsers: boolean;
  canApproveRequests: boolean;
  canViewAuditTrail: boolean;
  canExportData: boolean;
  canManageLeave: boolean;
  canApproveLeave: boolean;
  canViewOwnDataOnly: boolean;
}

export const ROLE_PERMISSIONS: Record<AppRole, RolePermissions> = {
  admin: {
    canAccessDashboard: true,
    canAccessDirectory: true,
    canAccessHR: true,
    canAccessAttendance: true,
    canAccessPayroll: true,
    canAccessDocuments: true,
    canAccessContracts: true,
    canAccessAssets: true,
    canAccessApprovals: true,
    canAccessReports: true,
    canAccessAudit: true,
    canAccessMasterData: true,
    canAccessCompanySetup: true,
    canAccessSettings: true,
    canAccessESS: true,
    canAccessCompliance: true,
    canAccessContacts: true,
    canViewSalaryDetails: true,
    canRunPayroll: true,
    canManageContracts: true,
    canEditMasterData: true,
    canManageUsers: true,
    canApproveRequests: true,
    canViewAuditTrail: true,
    canExportData: true,
    canManageLeave: true,
    canApproveLeave: true,
    canViewOwnDataOnly: false,
  },
  hr_manager: {
    canAccessDashboard: true,
    canAccessDirectory: true,
    canAccessHR: true,
    canAccessAttendance: true,
    canAccessPayroll: true,
    canAccessDocuments: true,
    canAccessContracts: true,
    canAccessAssets: true,
    canAccessApprovals: true,
    canAccessReports: true,
    canAccessAudit: true,
    canAccessMasterData: true,
    canAccessCompanySetup: false,
    canAccessSettings: false,
    canAccessESS: true,
    canAccessCompliance: true,
    canAccessContacts: true,
    canViewSalaryDetails: true,
    canRunPayroll: true,
    canManageContracts: true,
    canEditMasterData: true,
    canManageUsers: false,
    canApproveRequests: true,
    canViewAuditTrail: true,
    canExportData: true,
    canManageLeave: true,
    canApproveLeave: true,
    canViewOwnDataOnly: false,
  },
  payroll: {
    canAccessDashboard: true,
    canAccessDirectory: true,
    canAccessHR: false,
    canAccessAttendance: true,
    canAccessPayroll: true,
    canAccessDocuments: false,
    canAccessContracts: false,
    canAccessAssets: false,
    canAccessApprovals: false,
    canAccessReports: true,
    canAccessAudit: false,
    canAccessMasterData: false,
    canAccessCompanySetup: false,
    canAccessSettings: false,
    canAccessESS: false,
    canAccessCompliance: false,
    canAccessContacts: false,
    canViewSalaryDetails: true,
    canRunPayroll: true,
    canManageContracts: false,
    canEditMasterData: false,
    canManageUsers: false,
    canApproveRequests: false,
    canViewAuditTrail: false,
    canExportData: true,
    canManageLeave: false,
    canApproveLeave: false,
    canViewOwnDataOnly: false,
  },
  finance: {
    canAccessDashboard: true,
    canAccessDirectory: false,
    canAccessHR: false,
    canAccessAttendance: false,
    canAccessPayroll: true,
    canAccessDocuments: false,
    canAccessContracts: true,
    canAccessAssets: false,
    canAccessApprovals: false,
    canAccessReports: true,
    canAccessAudit: false,
    canAccessMasterData: false,
    canAccessCompanySetup: false,
    canAccessSettings: false,
    canAccessESS: false,
    canAccessCompliance: false,
    canAccessContacts: true,
    canViewSalaryDetails: true,
    canRunPayroll: false,
    canManageContracts: false,
    canEditMasterData: false,
    canManageUsers: false,
    canApproveRequests: true,
    canViewAuditTrail: false,
    canExportData: true,
    canManageLeave: false,
    canApproveLeave: false,
    canViewOwnDataOnly: false,
  },
  management: {
    canAccessDashboard: true,
    canAccessDirectory: true,
    canAccessHR: true,
    canAccessAttendance: true,
    canAccessPayroll: false,
    canAccessDocuments: true,
    canAccessContracts: true,
    canAccessAssets: true,
    canAccessApprovals: true,
    canAccessReports: true,
    canAccessAudit: false,
    canAccessMasterData: false,
    canAccessCompanySetup: false,
    canAccessSettings: false,
    canAccessESS: false,
    canAccessCompliance: true,
    canAccessContacts: true,
    canViewSalaryDetails: false,
    canRunPayroll: false,
    canManageContracts: false,
    canEditMasterData: false,
    canManageUsers: false,
    canApproveRequests: true,
    canViewAuditTrail: true,
    canExportData: true,
    canManageLeave: false,
    canApproveLeave: true,
    canViewOwnDataOnly: false,
  },
  department_head: {
    canAccessDashboard: true,
    canAccessDirectory: true,
    canAccessHR: false,
    canAccessAttendance: true,
    canAccessPayroll: false,
    canAccessDocuments: true,
    canAccessContracts: false,
    canAccessAssets: true,
    canAccessApprovals: true,
    canAccessReports: false,
    canAccessAudit: false,
    canAccessMasterData: false,
    canAccessCompanySetup: false,
    canAccessSettings: false,
    canAccessESS: true,
    canAccessCompliance: false,
    canAccessContacts: false,
    canViewSalaryDetails: false,
    canRunPayroll: false,
    canManageContracts: false,
    canEditMasterData: false,
    canManageUsers: false,
    canApproveRequests: true,
    canViewAuditTrail: false,
    canExportData: false,
    canManageLeave: false,
    canApproveLeave: true,
    canViewOwnDataOnly: false,
  },
  employee: {
    canAccessDashboard: false,
    canAccessDirectory: true,
    canAccessHR: false,
    canAccessAttendance: false,
    canAccessPayroll: false,
    canAccessDocuments: false,
    canAccessContracts: false,
    canAccessAssets: false,
    canAccessApprovals: false,
    canAccessReports: false,
    canAccessAudit: false,
    canAccessMasterData: false,
    canAccessCompanySetup: false,
    canAccessSettings: false,
    canAccessESS: true,
    canAccessCompliance: false,
    canAccessContacts: false,
    canViewSalaryDetails: false,
    canRunPayroll: false,
    canManageContracts: false,
    canEditMasterData: false,
    canManageUsers: false,
    canApproveRequests: false,
    canViewAuditTrail: false,
    canExportData: false,
    canManageLeave: false,
    canApproveLeave: false,
    canViewOwnDataOnly: true,
  },
  compliance: {
    canAccessDashboard: true,
    canAccessDirectory: false,
    canAccessHR: false,
    canAccessAttendance: false,
    canAccessPayroll: false,
    canAccessDocuments: true,
    canAccessContracts: true,
    canAccessAssets: false,
    canAccessApprovals: false,
    canAccessReports: true,
    canAccessAudit: true,
    canAccessMasterData: false,
    canAccessCompanySetup: false,
    canAccessSettings: false,
    canAccessESS: false,
    canAccessCompliance: true,
    canAccessContacts: false,
    canViewSalaryDetails: false,
    canRunPayroll: false,
    canManageContracts: false,
    canEditMasterData: false,
    canManageUsers: false,
    canApproveRequests: false,
    canViewAuditTrail: true,
    canExportData: true,
    canManageLeave: false,
    canApproveLeave: false,
    canViewOwnDataOnly: false,
  },
  approver: {
    canAccessDashboard: true,
    canAccessDirectory: true,
    canAccessHR: false,
    canAccessAttendance: true,
    canAccessPayroll: false,
    canAccessDocuments: true,
    canAccessContracts: false,
    canAccessAssets: false,
    canAccessApprovals: true,
    canAccessReports: false,
    canAccessAudit: false,
    canAccessMasterData: false,
    canAccessCompanySetup: false,
    canAccessSettings: false,
    canAccessESS: true,
    canAccessCompliance: false,
    canAccessContacts: false,
    canViewSalaryDetails: false,
    canRunPayroll: false,
    canManageContracts: false,
    canEditMasterData: false,
    canManageUsers: false,
    canApproveRequests: true,
    canViewAuditTrail: false,
    canExportData: false,
    canManageLeave: false,
    canApproveLeave: true,
    canViewOwnDataOnly: false,
  },
  read_only: {
    canAccessDashboard: true,
    canAccessDirectory: true,
    canAccessHR: false,
    canAccessAttendance: true,
    canAccessPayroll: false,
    canAccessDocuments: true,
    canAccessContracts: false,
    canAccessAssets: true,
    canAccessApprovals: false,
    canAccessReports: true,
    canAccessAudit: false,
    canAccessMasterData: false,
    canAccessCompanySetup: false,
    canAccessSettings: false,
    canAccessESS: false,
    canAccessCompliance: false,
    canAccessContacts: false,
    canViewSalaryDetails: false,
    canRunPayroll: false,
    canManageContracts: false,
    canEditMasterData: false,
    canManageUsers: false,
    canApproveRequests: false,
    canViewAuditTrail: false,
    canExportData: false,
    canManageLeave: false,
    canApproveLeave: false,
    canViewOwnDataOnly: false,
  },
};

export const ROLE_LABELS: Record<AppRole, string> = {
  admin: 'System Administrator',
  hr_manager: 'HR Manager',
  payroll: 'Payroll Officer',
  finance: 'Finance',
  management: 'Management',
  department_head: 'Department Head',
  employee: 'Employee',
  approver: 'Approver',
  read_only: 'Read-Only Viewer',
  compliance: 'Compliance Officer',
};

export const ROLE_COLORS: Record<AppRole, string> = {
  admin: 'bg-danger/10 text-danger',
  hr_manager: 'bg-primary/10 text-primary',
  payroll: 'bg-success/10 text-success',
  finance: 'bg-info/10 text-info',
  management: 'bg-warning/10 text-warning',
  department_head: 'bg-accent/10 text-accent-foreground',
  employee: 'bg-muted text-muted-foreground',
  approver: 'bg-[#6366F1]/10 text-[#6366F1]',
  read_only: 'bg-muted text-muted-foreground',
  compliance: 'bg-emerald-100 text-emerald-700',
};

export function getPermissions(role: AppRole | null | undefined): RolePermissions {
  if (!role) return ROLE_PERMISSIONS.read_only;
  return ROLE_PERMISSIONS[role] ?? ROLE_PERMISSIONS.read_only;
}

// Default landing page per role
export const ROLE_DEFAULT_ROUTE: Record<AppRole, string> = {
  admin: '/',
  hr_manager: '/',
  payroll: '/payroll',
  finance: '/payroll',
  management: '/',
  department_head: '/',
  employee: '/employee-self-service-portal',
  approver: '/approvals',
  read_only: '/',
  compliance: '/compliance-audit',
};
